import {useCallback, useMemo, useState} from 'react'
import {isEqual} from 'lodash'

type Observed<K> = {
  ignore?: K[]
}

type UseFiltersOptions<T extends object> = {
  defaultValue: T
  observed?: Observed<keyof T>
  onChange?: () => void
  onReset?: () => void
}

export type FiltersApi<T> = {
  value: T
  defaultValue: T
  pristine: boolean
  change: (newValue: Partial<T>) => void
  reset: () => void
}

/**
 * The goal of this hook is to concentrate filter related actions in the single API object.
 * This should help devs to drill single prop into children components.
 * @param observed - list of fields to calculate if the filters were changed or not.
 * @param onChange - callback to when we change filter values. Useful when we need to reset pagination.
 * @param onReset - callback to when we reset filter values. Useful when we need to reset pagination.
 */
export function useFilters<T extends object>(options: UseFiltersOptions<T>): FiltersApi<T> {
  const {defaultValue, observed, onChange, onReset} = options

  const [value, setValue] = useState(defaultValue)

  const pristine = useMemo(
    () => isEqual(getObservedValues(value, observed), getObservedValues(defaultValue, observed)),
    [defaultValue, observed, value],
  )

  const change = useCallback(
    (newValue: Partial<T>) => {
      onChange?.()
      setValue((previousValue) => ({...previousValue, ...newValue}))
    },
    [onChange, setValue],
  )

  const reset = useCallback(() => {
    onReset?.()
    setValue(defaultValue)
  }, [defaultValue, setValue, onReset])

  return useMemo(
    () => ({
      value,
      defaultValue,
      pristine,
      change,
      reset,
    }),
    [change, defaultValue, pristine, reset, value],
  )
}

function getObservedValues<T extends object>(values: T, observed?: Observed<keyof T>): Omit<T, keyof T> {
  const {ignore} = observed ?? {}

  if (ignore == null) return values
  else {
    const filteredEntries = Object.entries(values).filter(([key]) => !ignore.includes(key as keyof T))
    return Object.fromEntries(filteredEntries) as Omit<T, keyof T>
  }
}
